import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, openAuthedFile, type ApiResponse } from "@/api/client";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import type { Customer, Template, Communication, Order } from "@/components/customers/types";
import CustomerDetailHeader from "@/components/customers/CustomerDetailHeader";
import CustomerStatsCards from "@/components/customers/CustomerStatsCards";
import CustomerInfoPanel from "@/components/customers/CustomerInfoPanel";
import CustomerOrdersPanel from "@/components/customers/CustomerOrdersPanel";
import CustomerCommunications from "@/components/customers/CustomerCommunications";
import SendMessageModal from "@/components/customers/SendMessageModal";
import GdprPanel from "@/components/customers/GdprPanel";
import RecentCallsPanel from "@/components/common/RecentCallsPanel";
import InterestProfilePanel from "@/components/customers/InterestProfilePanel";
import NewslettersPanel from "@/components/customers/NewslettersPanel";

export default function CustomerDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Customer>>({});
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedTemplateType, setSelectedTemplateType] = useState<"email" | "pdf">("email");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["customer", id],
    queryFn: () => api.get<ApiResponse<Customer>>(`/api/admin/customers/${id}`),
    enabled: !!id,
  });

  const { data: templatesData } = useQuery({
    queryKey: ["templates-for-sending"],
    queryFn: () => api.get<ApiResponse<Template[]>>("/api/admin/templates?is_active=true"),
  });

  const { data: communicationsData } = useQuery({
    queryKey: ["customer-communications", id],
    queryFn: () =>
      api.get<ApiResponse<Communication[]>>(`/api/admin/customers/${id}/communications`),
    enabled: !!id,
  });

  const templates = templatesData?.data || [];
  const communications = communicationsData?.data || [];

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Customer>) =>
      api.put<ApiResponse<Customer>>(`/api/admin/customers/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer", id] });
      setIsEditing(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/api/admin/customers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      navigate("/customers");
    },
    // A customer with orders/messages is intentionally undeletable (FK RESTRICT); the API
    // explains why in a 409 — show it instead of failing silently.
    onError: (error: Error) => {
      toast.error(error.message || t("crm.customerDetail.deleteFailed"));
    },
  });

  const sendMutation = useMutation({
    mutationFn: (data: { templateId: string; orderId?: string }) =>
      api.post(`/api/admin/customers/${id}/send`, data),
    onSuccess: () => {
      toast.success(t("crm.customerDetail.messageSent"));
      queryClient.invalidateQueries({ queryKey: ["customer-communications", id] });
      setShowSendModal(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || t("crm.customerDetail.sendFailed"));
    },
  });

  const customer = data?.data;

  const handleOpenSendModal = (type: "email" | "pdf", order?: Order) => {
    setSelectedTemplateType(type);
    setSelectedOrder(order || null);
    setShowSendModal(true);
    setShowActionsMenu(false);
  };

  const handleDownloadDocument = async (docType: "contract" | "invoice", order: Order) => {
    try {
      toast.loading(
        docType === "contract"
          ? t("crm.customerDetail.generatingContract")
          : t("crm.customerDetail.generatingInvoice"),
        { id: "download" },
      );

      await openAuthedFile(
        `/api/admin/orders/${order.id}/document/${docType}`,
        `${docType === "contract" ? "contract" : "invoice"}_${order.orderNumber}.pdf`,
      );

      toast.success(
        docType === "contract"
          ? t("crm.customerDetail.contractDownloaded")
          : t("crm.customerDetail.invoiceDownloaded"),
        { id: "download" },
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("crm.customerDetail.downloadError"), {
        id: "download",
      });
    }
  };

  const handleEdit = () => {
    if (customer) {
      setEditData({
        firstName: customer.firstName,
        lastName: customer.lastName,
        email: customer.email,
        phone: customer.phone,
        street: customer.street,
        city: customer.city,
        zip: customer.zip,
        country: customer.country,
        birthDate: customer.birthDate,
        passportNumber: customer.passportNumber,
        passportExpiry: customer.passportExpiry,
        nationality: customer.nationality,
        notes: customer.notes,
        newsletterSubscribed: customer.newsletterSubscribed,
      });
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    updateMutation.mutate(editData);
  };

  const handleDelete = () => {
    if (confirm(t("crm.customerDetail.deleteConfirm"))) {
      deleteMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-primary-600 h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="card text-error-600 text-center">{t("crm.customerDetail.notFound")}</div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <CustomerDetailHeader
        customer={customer}
        isEditing={isEditing}
        isPendingUpdate={updateMutation.isPending}
        isPendingDelete={deleteMutation.isPending}
        showActionsMenu={showActionsMenu}
        onNavigateBack={() => navigate("/customers")}
        onSetShowActionsMenu={setShowActionsMenu}
        onOpenSendModal={handleOpenSendModal}
        onEdit={handleEdit}
        onSave={handleSave}
        onCancelEdit={() => setIsEditing(false)}
        onDelete={handleDelete}
      />

      <CustomerStatsCards customer={customer} />

      <div className="grid gap-6 lg:grid-cols-3">
        <CustomerInfoPanel
          customer={customer}
          isEditing={isEditing}
          editData={editData}
          onEditDataChange={setEditData}
        />

        <CustomerOrdersPanel
          customer={customer}
          onDownloadDocument={handleDownloadDocument}
          onOpenSendModal={handleOpenSendModal}
          onSelectOrder={setSelectedOrder}
        />
      </div>

      <RecentCallsPanel
        communications={communications}
        invalidateKeys={[["customer-communications", id]]}
      />

      <CustomerCommunications
        communications={communications}
        onOpenSendModal={handleOpenSendModal}
      />

      <InterestProfilePanel customerId={customer.id} />

      <NewslettersPanel customerId={customer.id} />

      <GdprPanel
        customerId={customer.id}
        anonymizedAt={customer.anonymizedAt}
        onAnonymized={() => queryClient.invalidateQueries({ queryKey: ["customer", id] })}
      />

      {showSendModal && (
        <SendMessageModal
          customer={customer}
          templates={templates}
          selectedTemplateType={selectedTemplateType}
          selectedOrder={selectedOrder}
          isPendingSend={sendMutation.isPending}
          onClose={() => setShowSendModal(false)}
          onSelectOrder={setSelectedOrder}
          onSend={(data) => sendMutation.mutate(data)}
        />
      )}
    </div>
  );
}
